import type { ReactNode } from 'react';
import type { FrameState } from '@erectus/shared';
import { STATE_LABEL, STATE_STYLE } from '@/scoring/frameState.js';
import { Icon } from './icons.js';
import { scoreColor } from './score.js';

/**
 * The posture status indicator.
 *
 * Accessibility rule (LEGAL §1 "색만으로 상태 표현 금지"): colour, an icon and a
 * text label always travel together, and the live region announces changes.
 */
export function StateBadge({
  state,
  size = 'md',
}: {
  state: FrameState;
  size?: 'md' | 'xl';
}): ReactNode {
  const style = STATE_STYLE[state];
  const big = size === 'xl';
  return (
    <div
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-3 rounded-2xl font-semibold ${
        big ? 'px-6 py-5 text-2xl' : 'px-3 py-1.5 text-sm'
      }`}
      style={{ backgroundColor: style.soft, color: style.color }}
    >
      <Icon name={style.icon} size={big ? 40 : 18} strokeWidth={big ? 2.2 : 2} />
      <span>{STATE_LABEL[state]}</span>
    </div>
  );
}

export function ScoreDial({ score }: { score: number }): ReactNode {
  const clamped = Math.max(0, Math.min(100, score));
  const color = scoreColor(clamped);
  return (
    <div className="flex items-center gap-4">
      <div
        className="grid size-24 place-items-center rounded-full"
        style={{
          background: `conic-gradient(${color} ${clamped}%, var(--color-line) ${clamped}% 100%)`,
        }}
        role="img"
        aria-label={`Posture score ${clamped} out of 100`}
      >
        <div className="grid size-[76px] place-items-center rounded-full bg-card">
          <span className="text-2xl font-bold" style={{ color }}>
            {clamped}
          </span>
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold">Posture score</p>
        <p className="text-xs text-muted">0–100, weighted across the session</p>
      </div>
    </div>
  );
}

/** Horizontal metric bar with good/warn/bad zones and the measured value. */
export function MetricBar({
  label,
  value,
  unit,
  min,
  max,
  goodFrom,
  goodTo,
  invert = false,
}: {
  label: string;
  value: number | null;
  unit: string;
  min: number;
  max: number;
  goodFrom: number;
  goodTo: number;
  invert?: boolean;
}): ReactNode {
  if (value === null) {
    return (
      <div className="py-2">
        <div className="flex justify-between text-sm">
          <span className="font-medium">{label}</span>
          <span className="text-muted">Not measured</span>
        </div>
      </div>
    );
  }
  const pct = (v: number): number => ((v - min) / (max - min)) * 100;
  const inGood = value >= goodFrom && value <= goodTo;
  return (
    <div className="py-2">
      <div className="flex justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span
          className="font-semibold"
          style={{
            color: inGood ? 'var(--color-state-good)' : 'var(--color-state-fhp)',
          }}
        >
          {value.toFixed(1)}
          {unit}
        </span>
      </div>
      <div className="relative mt-1.5 h-2 rounded-full bg-line">
        <div
          className="absolute inset-y-0 rounded-full"
          style={{
            left: `${Math.max(0, Math.min(100, pct(goodFrom)))}%`,
            width: `${Math.max(2, Math.min(100, pct(goodTo) - pct(goodFrom)))}%`,
            backgroundColor: 'var(--color-state-good-soft)',
          }}
        />
        <div
          className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
          style={{
            left: `${Math.max(0, Math.min(100, pct(value)))}%`,
            backgroundColor: inGood ? 'var(--color-state-good)' : 'var(--color-state-slouch)',
          }}
        />
      </div>
      <p className="mt-1 text-xs text-muted">
        {invert ? 'Lower is better' : 'Good range'}: {goodFrom}
        {unit} – {goodTo}
        {unit}
      </p>
    </div>
  );
}
